import { Engine } from '@babylonjs/core/Engines/engine';
import { RenderTargetTexture } from '@babylonjs/core/Materials/Textures/renderTargetTexture';
import { Effect } from '@babylonjs/core/Materials/effect';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { Color4 } from '@babylonjs/core/Maths';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { PostProcessRenderEffect } from '@babylonjs/core/PostProcesses/RenderPipeline/postProcessRenderEffect';
import { PostProcessRenderPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/postProcessRenderPipeline';
import { PostProcess } from '@babylonjs/core/PostProcesses/postProcess';
import { Scene } from '@babylonjs/core/scene';
import { SpaceNode } from '@models/classes/meshes/space-node';
import { KbColor } from '@models/classes/primitives/kb-color';
import { KbViewer } from '../kb-viewer';
import { CameraTools } from './camera-tools';
import maskFragmentShader from './shaders/mask-fragment-shader.fx';
import maskVertexShader from './shaders/mask-vertex-shader.fx';
import outlineFragmentShader from './shaders/outline-fragment-shader.fx';

export interface IHoverRenderer {
    hoverNode(node: SpaceNode): void;
    clearHover(): void;
}

Effect.ShadersStore['customMaskVertexShader'] = maskVertexShader;
Effect.ShadersStore['customMaskFragmentShader'] = maskFragmentShader;
Effect.ShadersStore['simpleOutlineFragmentShader'] = outlineFragmentShader;

export class Highlighter {
    highlightPipeline: PostProcessRenderPipeline;
    postProcess: PostProcess;
    renderTarget: RenderTargetTexture;

    private engine: Engine;

    constructor(
        private kbViewer: KbViewer,
        private scene: Scene,
        public defaultHighlightColor: KbColor,
        cameraManager: CameraTools
    ) {
        this.engine = scene.getEngine();
        this.highlightPipeline = new PostProcessRenderPipeline(this.engine, 'highlightPipeline');

        this.renderTarget = new RenderTargetTexture('Highlight render target', 1024, scene, { samples: 2 });
        this.renderTarget.renderList = [];
        this.renderTarget.clearColor = new Color4(0, 0, 0, 0);
        scene.customRenderTargets.push(this.renderTarget);

        this.postProcess = new PostProcess(
            'Highlighter post process',
            'simpleOutline',
            ['screenSize', 'threshold', 'lineWidth', 'debug'],
            ['maskSampler'],
            1.0,
            null,
            undefined,
            this.engine,
            true
        );
        // This may help with the highlight separating from the scene meshes, if we can ge the highlight to combine with the scene
        // this.postProcess.autoClear = false;
        // this.postProcess.alphaMode = Engine.ALPHA_COMBINE;
        this.postProcess.samples = 4;
        this.postProcess.onApply = effect => {
            effect.setFloat2('screenSize', this.postProcess.width, this.postProcess.height);
            effect.setFloat('threshold', 0.3);
            effect.setFloat('lineWidth', 4.0);
            effect.setBool('debug', false);
            effect.setTexture('maskSampler', this.renderTarget);
            (window as any).enableHighlightDebug = () => {
                enableHighlightDebug(effect);
            };
        };
        const highlightEffect = new PostProcessRenderEffect(
            this.engine,
            'highlightEffect',
            () => [this.postProcess],
            false
        );
        this.highlightPipeline.addEffect(highlightEffect);
        this.scene.postProcessRenderPipelineManager.addPipeline(this.highlightPipeline);
        cameraManager.setPipeline(this.highlightPipeline);

        function enableHighlightDebug(effect: Effect) {
            effect.setBool('debug', true);
        }
    }

    private colorMap = new Map<
        string,
        {
            material: ShaderMaterial;
            meshes: Set<AbstractMesh>;
        }
    >();
    private nodeColors = new Map<AbstractMesh, string>();
    private activeHover: SpaceNode | undefined;
    private highlighterDb = new Map<string, KbColor>();

    public has(node: SpaceNode) {
        return this.highlighterDb.has(node._dynamicId);
    }

    public add(node: SpaceNode, color: KbColor = this.defaultHighlightColor) {
        this.startPostProcess();

        const renderNode = this.kbViewer.getRendererNode(node);
        this.highlighterDb.set(node._dynamicId, color);
        this.addHighlightToRendered(renderNode, color);
    }

    public remove(node: SpaceNode) {
        if (this.highlighterDb.has(node._dynamicId)) {
            const renderNode = this.kbViewer.getRendererNode(node);
            if (this.activeHover?._dynamicId !== node._dynamicId) {
                // Don't remove the highlight from a hover. Once the hover clears the highlight will be cleared.
                this.removeHighlightFromRendered(renderNode);
                this.highlighterDb.delete(node._dynamicId);
            }
        }
    }

    public hover(node: SpaceNode) {
        if (node != this.activeHover) {
            this.startPostProcess();
            this.clearHover();
            this.activeHover = node;
            const renderNode = this.kbViewer.getRendererNode(node);
            this.addHighlightToRendered(renderNode, this.defaultHighlightColor);
            this.kbViewer.canvas!.style.cursor = 'pointer';
            this.scene.defaultCursor = 'pointer';
            this.kbViewer.forceRenderOnNextFrame = true;
        }
    }

    public clearHover() {
        if (this.activeHover) {
            const renderNode = this.kbViewer.getRendererNode(this.activeHover);
            this.removeHighlightFromRendered(renderNode);
            //if the node had a highlight previously, restore it
            if (this.highlighterDb.has(this.activeHover._dynamicId)) {
                const color = this.highlighterDb.get(this.activeHover._dynamicId)!;
                this.addHighlightToRendered(renderNode, color);
            }
            // const targets = renderNode instanceof AbstractMesh ? [renderNode] : renderNode.getChildMeshes();
            // for (const target of targets) {
            //     if (this.highlighterDb.has(target)) {
            //         const color = this.highlighterDb.get(target)!;
            //         this.addHighlightToRendered(target, color);
            //     }
            // }
            this.activeHover = undefined;
            this.kbViewer.canvas!.style.cursor = 'default';
            this.scene.defaultCursor = 'default';
            this.kbViewer.forceRenderOnNextFrame = true;
        }
    }

    public clearAll() {
        this.colorMap.forEach(entry => {
            this.renderTarget.setMaterialForRendering(Array.from(entry.meshes), undefined);
        });
        if (this.renderTarget.renderList) {
            while (this.renderTarget.renderList.length > 0) {
                this.renderTarget.renderList.pop();
            }
        }
        this.nodeColors.clear();
        this.highlighterDb.clear();
        this.colorMap.clear();
        this.activeHover = undefined;
    }

    private addHighlightToRendered(node: TransformNode, color: KbColor) {
        const targets = node instanceof AbstractMesh ? [node] : node.getChildMeshes();
        for (const renderNode of targets) {
            if (this.renderTarget.renderList?.contains(renderNode) === false) {
                if (!this.renderTarget.renderList) {
                    this.renderTarget.renderList = [];
                }
                this.renderTarget.renderList.push(renderNode);
            }
            let colorMapKey = '';

            if (color) {
                colorMapKey = this.colorToString(color);
            } else {
                colorMapKey = this.colorToString(this.defaultHighlightColor);
            }

            this.addNodeToColorMap(colorMapKey, renderNode, color);
            this.nodeColors.set(renderNode, colorMapKey);
        }
    }

    private removeHighlightFromRendered(node: TransformNode) {
        const targets = node instanceof AbstractMesh ? [node] : node.getChildMeshes();
        for (const renderNode of targets) {
            const colorKey = this.nodeColors.get(renderNode);
            this.renderTarget.renderList?.remove(renderNode);
            if (colorKey) {
                this.removeNodeFromColorMap(colorKey, renderNode);
                this.nodeColors.delete(renderNode);
            }
        }
    }

    private addNodeToColorMap(colorMapKey: string, node: AbstractMesh, color?: KbColor) {
        let colorMapEntry = this.colorMap.get(colorMapKey);
        if (!colorMapEntry) {
            const newShader = new ShaderMaterial('customMaskMat_' + colorMapKey, this.scene, 'customMask', {
                attributes: ['position', 'uv'],
                uniforms: ['worldViewProjection', 'color', 'lineColor'],
            });
            if (color) {
                newShader.setColor4('lineColor', color.toColor4());
            } else {
                newShader.setColor4('lineColor', this.defaultHighlightColor.toColor4());
            }
            colorMapEntry = {
                material: newShader,
                meshes: new Set(),
            };
        }
        colorMapEntry.meshes.add(node);
        this.renderTarget.setMaterialForRendering(Array.from(colorMapEntry.meshes), colorMapEntry.material);
    }

    private removeNodeFromColorMap(colorMapKey: string, node: AbstractMesh) {
        const colorMapEntry = this.colorMap.get(colorMapKey);
        if (colorMapEntry) {
            colorMapEntry.meshes.delete(node);
            this.renderTarget.setMaterialForRendering(node, undefined);
        }
    }

    private colorToString(color: KbColor) {
        return color.cssString();
    }

    private startPostProcess() {}
}
